import { AfterViewInit, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, input, OnDestroy, signal, viewChild } from '@angular/core';
import { DsyInputDropdown } from '@dsy/dsyui';
import { DsyListElement } from '../../../../models/dsy-list-element.model';

type DsyDropdownHost = HTMLElement & { isOpen?: boolean; updateComplete?: Promise<unknown> };
type DsyListCustomConf = NonNullable<InstanceType<typeof DsyInputDropdown>['customDsyListConfObject']>;
type SearchableEntry = { entry: DsyListElement; normalizedLabel: string };

const DEBOUNCE_DELAY = 150;
const SLICE_ENTRIES = 100;

@Component({
  selector: 'fdr-past-performance-ui-searchable-dropdown',
  templateUrl: './searchable-dropdown.component.html',
  styleUrl: './searchable-dropdown.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: { style: 'display: contents' },
})
export class SearchableDropdownComponent implements AfterViewInit, OnDestroy {
  readonly entries = input.required<DsyListElement[]>();
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly searchPlaceholder = input<string>('Search...');
  readonly exclusiveSelection = input<boolean>(false);
  readonly closesOnSelection = input<boolean>(false);
  readonly customDsyListConfObject = input<DsyListCustomConf>({});
  readonly renderCounterTag = input<boolean>(false);
  readonly counterTagConfig = input<unknown>();

  protected readonly containerStyle = 'padding-top: 48px;';
  protected readonly search = signal('');
  private readonly debouncedSearch = signal('');
  protected readonly isOpen = signal(false);

  // Normalizes labels for case-insensitive filtering.
  private readonly normalizedEntries = computed<SearchableEntry[]>(() =>
    this.entries().map(entry => ({
      entry,
      normalizedLabel: entry.labelContent?.toLowerCase() ?? '',
    }))
  );

  protected readonly ddRef = viewChild<ElementRef<DsyDropdownHost>>('dd');
  protected readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  // Filters entries while keeping the selected entry visible.
  protected readonly filteredEntries = computed<DsyListElement[]>(() => {
    const term = this.debouncedSearch().toLowerCase().trim();
    const entries = this.normalizedEntries();
    const selectedEntry = entries.find(({ entry }) => entry.isSelected);
    const matchingEntries = term
      ? entries.filter(({ normalizedLabel }) => normalizedLabel.includes(term))
      : entries;
    const limitedEntries = matchingEntries.slice(0, SLICE_ENTRIES);

    if (!selectedEntry) return limitedEntries.map(({ entry }) => entry);

    const selectedEntryIsVisible = limitedEntries.includes(selectedEntry);
    const entriesToDisplay = selectedEntryIsVisible ? limitedEntries : [selectedEntry, ...limitedEntries];

    return entriesToDisplay.map(({ entry }) => entry);
  });

  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private resizeObs?: ResizeObserver;
  private searchDebounceTimer?: ReturnType<typeof setTimeout>;

  private readonly syncOpen = () => {
    const el = this.ddRef()?.nativeElement;
    const open = !!el?.isOpen;
    const wasOpened = !this.isOpen() && open;

    this.isOpen.set(open);
    if (wasOpened) this.handleDropdownOpened();
  };

  // Resets search state and focuses the dropdown input.
  private handleDropdownOpened() {
    this.clearSearchDebounce();
    this.search.set('');
    this.debouncedSearch.set('');
    this.clearSearchInput();

    setTimeout(() => {
      const input = this.searchInputRef()?.nativeElement;
      input?.focus();
      this.clearSearchInput();
    }, 0);
  }

  // Clears the native input value.
  private clearSearchInput() {
    const input = this.searchInputRef()?.nativeElement;
    if (input) input.value = '';
  }

  private readonly handleOutsideMousedown = (event: MouseEvent) => {
    if (!this.isOpen()) return;
    const host = this.ddRef()?.nativeElement;
    if (!host) return;
    const path = event.composedPath();
    if (path.includes(this.elRef.nativeElement) || path.includes(host)) return;
    host.isOpen = false;
    this.isOpen.set(false);
  };

  private async observePanelWidth() {
    const host = this.ddRef()?.nativeElement;
    if (!host) return;
    if (host.updateComplete) await host.updateComplete;
    const panel = host.shadowRoot?.querySelector('#dropdown-wrapper') as HTMLElement | null;
    if (!panel) return;
    const update = () => {
      const w = panel.getBoundingClientRect().width;
      this.elRef.nativeElement.style.setProperty('--dd-panel-width', `${w}px`);
    };
    this.resizeObs = new ResizeObserver(update);
    this.resizeObs.observe(panel);
    update();
  }

  ngAfterViewInit() {
    document.addEventListener('click', this.syncOpen);
    document.addEventListener('mousedown', this.handleOutsideMousedown, true);
    this.observePanelWidth();
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.syncOpen);
    document.removeEventListener('mousedown', this.handleOutsideMousedown, true);
    this.resizeObs?.disconnect();
    this.clearSearchDebounce();
  }

  protected onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.search.set(value);
    this.debounceSearch(value);
  }

  // Schedules the delayed search update.
  private debounceSearch(value: string) {
    this.clearSearchDebounce();
    this.searchDebounceTimer = setTimeout(() => {
      this.debouncedSearch.set(value);
      this.searchDebounceTimer = undefined;
    }, DEBOUNCE_DELAY);
  }

  // Cancels any pending search update.
  private clearSearchDebounce() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = undefined;
    }
  }

  protected stopEvent(event: Event) {
    event.stopPropagation();
  }
}
